// A collapsible "how to read this report" panel for the Backend Reporting reports (Eric, 2026-10-18): what the report
// is for, where every number comes from, what each term means and how to use the page, written for someone opening
// it for the first time. Minimised by default (Eric, 2026-10-18); the choice is remembered per report in this browser.
import React, { useEffect, useState } from 'react';
import { Card, BlockStack, InlineStack, InlineGrid, Text, Button, Collapsible, List } from '@shopify/polaris';

const storeKey = id => `reportGuide.${id}`;

export default function ReportGuide({ id, title = 'About this report', summary, sections = [] }) {
    const [open, setOpen] = useState(() => {
        try { const v = window.localStorage.getItem(storeKey(id)); return v === null ? false : v === '1'; } catch (_e) { return false; }
    });
    const toggle = () => setOpen(o => {
        const n = !o;
        try { window.localStorage.setItem(storeKey(id), n ? '1' : '0'); } catch (_e) { /* private window */ }
        return n;
    });
    // Showing or hiding the guide moves everything below it, and the report's bottom table sizes itself to end at the
    // bottom of the window: tell it to measure again once the panel has finished opening or closing.
    useEffect(() => {
        const fire = () => window.dispatchEvent(new Event('resize'));
        const ts = [0, 80, 200, 400].map(ms => setTimeout(fire, ms));
        return () => ts.forEach(clearTimeout);
    }, [open]);
    return (
        <Card>
            <BlockStack gap="200">
                <InlineStack align="space-between" blockAlign="center" wrap={false} gap="300">
                    <BlockStack gap="050">
                        <Text as="h2" variant="headingMd">{title}</Text>
                        {summary && <Text as="p" variant="bodyMd">{summary}</Text>}
                    </BlockStack>
                    <Button variant="plain" onClick={toggle} ariaExpanded={open} ariaControls={`guide-${id}`}><span style={{ whiteSpace: 'nowrap' }}>{open ? 'Hide ▲' : 'Show ▼'}</span></Button>
                </InlineStack>
                <Collapsible open={open} id={`guide-${id}`} transition={{ duration: '150ms', timingFunction: 'ease-in-out' }}>
                    <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
                        {sections.map(sec => (
                            <BlockStack gap="150" key={sec.heading}>
                                <Text as="h3" variant="headingSm">{sec.heading}</Text>
                                {sec.text && <Text as="p" variant="bodyMd">{sec.text}</Text>}
                                {sec.items && (
                                    <List type="bullet">
                                        {sec.items.map((it, i) => <List.Item key={i}>{it}</List.Item>)}
                                    </List>
                                )}
                            </BlockStack>
                        ))}
                    </InlineGrid>
                </Collapsible>
            </BlockStack>
        </Card>
    );
}
